/**
 * What the *affiliate's* portal shows, and how.
 *
 * The rules every portal shares — how an amount is written, how a provisional
 * figure marks itself, how a report states its age — live in the portal kit.
 * What stays here is what only an affiliate ever sees:
 *
 *   §12.5c the clicks that did not count, and why
 *   §31.1a "paid" is the operator's claim, never our observation
 *   §6.3g  three portal states, and what each one means for the money
 *   §17.8a what an affiliate may see about a player, and what they may not
 *   §14.5d the attribution model in plain language, never as a config key
 */

import { UNAVAILABLE, countryLabel, formatCount, formatDate, formatDateTime, formatDay, formatMoney, geoLabel, periodDays, sortRows, sortableCell, stripRuleReferences, type SortDir } from '@affdice/portal-kit';

import { AFFILIATE_MESSAGES, t, type AffiliateMessageKey } from './messages.ts';

/**
 * §12.5c — bot and deduplicated clicks are shown to the affiliate with the
 * reason. "Affiliates buying traffic need this to police their own sources;
 * hiding it makes the platform look like it is shaving clicks."
 *
 * So the funnel shows what counted **and** what did not, and says why the two
 * differ rather than leaving the affiliate to compare against their own tracker
 * and conclude we are wrong.
 */
export interface FunnelView {
  readonly day: string;
  /**
   * §12.13/§26.11 — the day's counted impressions, the figure CTR reads
   * against. Absent, not zero, on a response that carries none: a day with no
   * impression row and a day nothing was served are the same row here, and
   * only the screen can say which reading applies (see `dashboard`).
   */
  readonly impressions?: number;
  readonly counted: number;
  readonly excluded: number;
  readonly excludedNote?: string;
  readonly registrations: number;
  /** §1.4a — the reporting count. It never goes down. */
  readonly ftds: number;
  /** §1.4a — the payable count, shown separately because they diverge. */
  readonly payableFtds: number;
}

export function funnelRow(row: {
  day: string;
  clicks: number;
  bot_clicks: number;
  deduplicated_clicks: number;
  geo_blocked_clicks: number;
  /** How many of those had no country at all — see the note below. */
  geo_unplaced_clicks?: number;
  os_blocked_clicks: number;
  device_blocked_clicks: number;
  /** #29 — refused by the campaign's own targeting rules. */
  targeting_blocked_clicks: number;
  registrations: number;
  ftds: number;
  payable_ftds: number;
  /** §12.13 — absent on an older response; see `FunnelView.impressions`. */
  impressions?: number;
}): FunnelView {
  const excluded =
    row.bot_clicks + row.deduplicated_clicks + row.geo_blocked_clicks +
    row.os_blocked_clicks + row.device_blocked_clicks + (row.targeting_blocked_clicks ?? 0);

  // Only the causes that actually occurred. "0 classified as automated" is not
  // a reason, and a note that lists absent causes trains the reader to skim the
  // one line on the screen that exists to be read.
  const causes: string[] = [];
  if (row.bot_clicks > 0) causes.push(`${row.bot_clicks} classified as automated`);
  if (row.deduplicated_clicks > 0) {
    causes.push(
      `${row.deduplicated_clicks} repeated within the deduplication window — ` +
        `a repeat still keeps your attribution`,
    );
  }
  /*
   * Two different things arrive as GEO_BLOCKED and they ask the reader for
   * opposite responses.
   *
   * A click from a country the campaign does not run in is about the traffic,
   * and an affiliate reading it should look at their sources. A click nobody
   * could place is about the platform: the tracking edge resolved no country,
   * so the eligibility test refused it on principle. Calling that second one
   * "outside the campaign's licensed territory" sends somebody to audit
   * traffic that was never the problem — and where the ingress resolves no
   * country at all, it says it about every click they will ever send.
   */
  const unplaced = row.geo_unplaced_clicks ?? 0;
  const offTerritory = row.geo_blocked_clicks - unplaced;
  if (offTerritory > 0) {
    causes.push(`${offTerritory} outside the campaign's licensed territory`);
  }
  if (unplaced > 0) {
    causes.push(
      `${unplaced} we could not place — no country reached the tracking edge, ` +
        'which is a setup problem on our side rather than anything about your traffic',
    );
  }
  if (row.os_blocked_clicks > 0) {
    causes.push(`${row.os_blocked_clicks} on an operating system the campaign doesn't target`);
  }
  if (row.device_blocked_clicks > 0) {
    causes.push(`${row.device_blocked_clicks} on a device class the campaign doesn't target`);
  }
  if (row.targeting_blocked_clicks > 0) {
    causes.push(`${row.targeting_blocked_clicks} outside the campaign's targeting rules`);
  }

  return {
    day: row.day,
    // Carried through rather than defaulted to 0: a response with no
    // impression figure at all and a day on which none was served are
    // different claims, and only the first is unknowable from here.
    ...(row.impressions === undefined ? {} : { impressions: row.impressions }),
    counted: row.clicks,
    excluded,
    ...(causes.length > 0 ? { excludedNote: `${causes.join('; ')}.` } : {}),
    registrations: row.registrations,
    ftds: row.ftds,
    payableFtds: row.payable_ftds,
  };
}

/**
 * §31.1a — "'Paid' here is a **claim by the operator**, not an observation by
 * the platform. Every surface showing it MUST make that distinction visible."
 *
 * The portal never renders the word "Paid" alone. It renders who said so.
 */
export interface PaymentView {
  readonly documentNo: string;
  readonly amount: string;
  readonly dueOn: string;
  readonly status: string;
  /**
   * §31.2a — whether this document is past due, as a fact rather than as a
   * word. The screen used to read it off `status.startsWith('Outstanding')`,
   * which stops being true the moment the status is written in the reader's
   * language: the row keeps its overdue styling in English and quietly loses
   * it in Spanish, on the screen whose whole job is saying how late a payment
   * is.
   */
  readonly overdue: boolean;
  readonly caveat?: string;
}

/**
 * §30.1a — a statement row, including the periods that earned nothing.
 *
 * The zero rows are why this list exists, so they are formatted exactly like
 * the others rather than collapsed into "no activity". A zero an affiliate can
 * read is a period the platform accounted for; an absent row is a period they
 * cannot tell apart from a failure.
 */
export interface StatementRowView {
  readonly period: string;
  readonly statementNo: string;
  readonly earned: string;
  /** §30.2a — earned counts held money; this is the part not yet payable. */
  readonly held: string;
  readonly payable: string;
  readonly carriedForward: string;
  readonly invoiceNo: string;
  /** §30.3 — which version this is, in the affiliate's words. */
  readonly version: string;
}

/**
 * §42.7 — one line of the affiliate's own history, in their words.
 *
 * The audit record's `action` is an internal constant — AFFILIATE_STATUS_CHANGED
 * — and printing it is asking the reader to learn our vocabulary to find out
 * what happened to their money. §33.4a's standard applies: "our system says so"
 * is not an acceptable answer, and neither is our enum.
 *
 * An unmapped action falls back to a plain sentence rather than the constant,
 * because a new action type appearing on this screen as SCREAMING_SNAKE_CASE is
 * a bug the affiliate sees before we do.
 */
const HISTORY_ACTIONS: ReadonlySet<string> = new Set([
  'AFFILIATE_APPROVED',
  'AFFILIATE_REJECTED',
  'AFFILIATE_STATUS_CHANGED',
  'DEAL_PROPOSED',
  'DEAL_ACCEPTED',
  'DEAL_VERSION_ACCEPTED',
  'ADJUSTMENT_RAISED',
  'ADJUSTMENT_APPROVED',
  'INVOICE_APPROVED',
  'SETTLEMENT_MARKED',
  'SETTLEMENT_UNMARKED',
  'MANAGER_ASSIGNED',
]);

export interface HistoryLineView {
  readonly what: string;
  readonly when: string;
  readonly why: string;
  readonly by: string;
}

export function historyLine(row: {
  action: string;
  reason: string | null;
  at: string;
  by_role: string;
}): HistoryLineView {
  return {
    what: HISTORY_ACTIONS.has(row.action)
      ? t(`history.${row.action}` as AffiliateMessageKey)
      : t('history.unknown'),
    when: formatDate(row.at),
    // §42.1 — the reason is required when the change is made, so it exists.
    // Saying none was given is different from saying nothing.
    why: row.reason ?? t('history.noReason'),
    by: row.by_role === 'System' ? t('history.by.system') : t('history.by.operator'),
  };
}

export function statementRow(row: {
  statement_no: string;
  currency: string;
  earned_minor: string;
  held_minor: string;
  payable_minor: string;
  carried_forward_minor: string;
  period_from: string;
  period_to: string;
  is_correction: boolean;
  superseded: boolean;
  invoice_number: string | null;
}): StatementRowView {
  const money = (minor: string) => formatMoney({ amount_minor: minor, currency: row.currency });
  return {
    // The days it covers, not the instants stored: the window is half-open,
    // and a boundary converted into the reader's zone moves a day.
    period: (() => {
      const { first, last } = periodDays(row.period_from, row.period_to);
      return t('statements.periodRange', { from: formatDay(first), to: formatDay(last) });
    })(),
    statementNo: row.statement_no,
    earned: money(row.earned_minor),
    held: money(row.held_minor),
    payable: money(row.payable_minor),
    carriedForward: money(row.carried_forward_minor),
    // §30.4 — an invoice exists only where the balance cleared the minimum, and
    // the absence is information: it says the balance carried rather than that
    // something failed.
    invoiceNo: row.invoice_number ?? t('statement.carriedForward'),
    version: row.superseded
      ? t('statement.superseded')
      : row.is_correction
        ? t('statement.correction')
        : t('statement.issued'),
  };
}

export function paymentRow(row: {
  document_no: string;
  net_minor: string;
  currency: string;
  due_at: string;
  operator_marked_paid_at: string | null;
  days_outstanding: number | null;
}): PaymentView {
  if (row.operator_marked_paid_at) {
    return {
      documentNo: row.document_no,
      amount: formatMoney({ amount_minor: row.net_minor, currency: row.currency }),
      dueOn: formatDate(row.due_at),
      overdue: false,
      // Never "Paid". The operator says so; we did not see it.
      // `paid_on` is a DATE — the day the operator says it paid — so it is
      // printed as that day, never shifted into the viewer's zone.
      status: t('payment.operatorMarkedPaid', {
        date: formatDay(row.operator_marked_paid_at),
      }),
      caveat: t('payment.caveat'),
    };
  }

  const overdue = (row.days_outstanding ?? 0) > 0;
  return {
    documentNo: row.document_no,
    amount: formatMoney({ amount_minor: row.net_minor, currency: row.currency }),
    dueOn: formatDate(row.due_at),
    overdue,
    status: overdue
      ? t(
          row.days_outstanding === 1 ? 'payment.overdue.one' : 'payment.overdue.other',
          { n: row.days_outstanding ?? 0 },
        )
      : t('payment.awaiting'),
  };
}

/**
 * §6.3g — the three states an affiliate sees, and what each one means for their
 * money.
 *
 * The state alone is not enough: "Under Review" is the same word for an
 * application being assessed and for a live affiliate whose traffic is being
 * examined, and those mean very different things about whether the money keeps
 * accruing. §6.3a is emphatic that suspension is a review rather than a verdict,
 * so the explanation says so.
 */
export function explainStatus(
  portalStatus: string,
): { code: string; status: string; meaning: string } {
  // `code` is the server's own word, carried through untranslated so a caller
  // can branch on it. Every such branch used to read `status` — which is now
  // the *reader's* word, and comparing "Activa" to 'Active' is how a healthy
  // account gets a caution banner in Spanish and not in English.
  switch (portalStatus) {
    case 'Active':
      return {
        code: portalStatus,
        status: t('status.active'),
        meaning: t('status.active.meaning'),
      };
    case 'Under Review':
      return {
        code: portalStatus,
        status: t('status.underReview'),
        meaning: t('status.underReview.meaning'),
      };
    case 'Ended':
      return {
        code: portalStatus,
        status: t('status.ended'),
        meaning: t('status.ended.meaning'),
      };
    default:
      // The state itself is whatever the server sent, unchanged: an unknown one
      // is not ours to put words to.
      return {
        code: portalStatus,
        status: portalStatus,
        meaning: t('status.unknown.meaning'),
      };
  }
}

/**
 * §17.8a — the portal renders only the fields the rule permits.
 *
 * The API refuses the rest, and this refuses them again. Two layers because a
 * field that should never reach an affiliate reaching them through a UI that
 * "just displays what the API returned" is still a breach, and the API is one
 * change away from returning more.
 */
const PLAYER_COLUMNS = [
  'player_ref',
  /*
   * Where this customer is, as the brand verified it at registration
   * (§36.2b(iii)) — the country that actually decides their eligibility.
   *
   * The report carried only `join_country`, which is the country the *click*
   * came from. On a deployment whose ingress resolves nothing that is null for
   * every row, so the report showed no country at all while the API was
   * returning "NG" on the very same rows (owner, 2026-09-29: "country disl
   * pahije"). Both are here now, and they are two different questions —
   * somebody clicks on holiday and registers at home.
   */
  'country',
  'registered_at',
  'first_deposit_at',
  /*
   * How much traffic this player took, and what they have been worth. The
   * reference console's customer-activity report carries clicks and a revenue
   * figure beside the deposits, and ours carried neither — a row said what
   * somebody deposited and never what it cost to get them (owner,
   * 2026-09-29).
   *
   * Impressions, signups and chargebacks are on that report too and are not
   * here: a signup is what every row already is, impressions are not
   * attributable to one person, and no chargeback figure exists per player in
   * this projection. Inventing three columns of zeroes would read as data.
   */
  'clicks',
  'deposits_minor',
  'withdrawals_minor',
  'turnover_minor',
  'ggr_minor',
  'ngr_minor',
  'commission_minor',
  'currency',
  /*
   * How this player was acquired — the registration detail the reference
   * console puts on its customer-registrations report. Facts about the
   * affiliate's own traffic, not about the person: §17.8a's list is name,
   * email, phone, address, documents, IP, RG and VIP, and none of these is
   * one of those.
   */
  'join_country',
  'referring_url',
  'payload',
  'channel',
  'brand',
  'landing_page',
  'campaign_group',
  'media',
] as const;

/**
 * One player's row, with its money formatted.
 *
 * The amounts arrive as minor units — `deposits_minor: "200000"` is ₦2,000.00
 * — and the screen printed them straight through, so an affiliate read
 * ₦200,000 where the truth was ₦2,000: every money column on this report was
 * wrong by the currency's own scale. Found 2026-09-29 against staging.
 *
 * Formatted here rather than on the screen because this is the layer that is
 * tested, and §1.3a wants money formatted once — two screens formatting the
 * same figure is how two screens come to disagree about it.
 *
 * A null stays null rather than becoming a zero: `ngr_minor` is null for a
 * player no deal version has priced, and "nothing yet" is not "nothing".
 */
/** Every column of the player report a reader may order it by. */
export const PLAYER_SORTABLE = PLAYER_COLUMNS;

/** The kit's own; re-exported so the report keeps importing it from here. */
export { sortableCell } from '@affdice/portal-kit';

/** What each column of the player report orders by. */
export const playerSortValues: Readonly<
  Record<string, (row: Record<string, unknown>) => string | number | null>
> = Object.fromEntries(
  PLAYER_SORTABLE.map((c) => [c, (row: Record<string, unknown>) => sortableCell(row[c])]),
);

/**
 * The report's rows in the order the reader asked for, formatted once they are
 * in it — not the other way round.
 *
 * Ordering the formatted rows ordered the *rendering* of each value. A
 * date-time printed month-first put 2024 above 2026, because the year is
 * compared last; a twelve-hour clock put 9 PM between 1 and 3; and an amount
 * printed with its currency symbol in front sorted as text rather than as the
 * number it is. The row the API sent holds an ISO timestamp, which sorts
 * chronologically, and a minor-unit amount, which sorts as a number.
 */
export function playersInOrder(
  rows: readonly Record<string, unknown>[],
  key: string | null,
  dir: SortDir,
): Record<string, unknown>[] {
  const value = key === null ? undefined : playerSortValues[key];
  const ordered = value ? sortRows(rows, value, dir) : rows;
  return ordered.map((row) => playerColumns(row));
}

/**
 * The totals row under the player report.
 *
 * Summed from the raw rows, never from the formatted ones: adding up strings
 * that have already been rounded for display is how a total comes to disagree
 * with its own column. Money totals only where every row shares a currency —
 * §21.4a, a sum across two currencies is not a number.
 */
export function playerColumnTotals(
  rows: readonly Record<string, unknown>[],
): Record<string, string> {
  if (rows.length === 0) return {};
  const currencies = new Set(rows.map((r) => String(r['currency'] ?? '')));
  const oneCurrency = currencies.size === 1 ? [...currencies][0] ?? '' : null;
  const out: Record<string, string> = {};

  const clicks = rows.reduce((n, r) => n + (Number(r['clicks']) || 0), 0);
  if (rows.some((r) => r['clicks'] !== undefined)) out['clicks'] = formatCount(clicks);

  for (const key of ['deposits_minor', 'withdrawals_minor', 'turnover_minor', 'ggr_minor', 'commission_minor']) {
    if (!rows.some((r) => r[key] !== undefined && r[key] !== null)) continue;
    if (!oneCurrency) {
      // Said rather than added: two currencies in one column have no sum.
      out[key] = UNAVAILABLE;
      continue;
    }
    const total = rows.reduce((n, r) => n + BigInt(String(r[key] ?? '0') || '0'), 0n);
    out[key] = formatMoney({ amount_minor: total.toString(), currency: oneCurrency });
  }
  return out;
}

export function playerColumns(row: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const currency = typeof row['currency'] === 'string' ? row['currency'] : null;
  for (const key of PLAYER_COLUMNS) {
    if (!(key in row)) continue;
    const value = row[key];
    if (key.endsWith('_minor') && currency && value !== null && value !== undefined) {
      out[key] = formatMoney({ amount_minor: String(value), currency });
    } else if (key.endsWith('country') && typeof value === 'string' && value) {
      // Flag and name, not the two-letter code the database stores.
      out[key] = countryLabel(value);
    } else if (key.endsWith('_at') && typeof value === 'string' && value) {
      // The wire format is not a reading format: this report printed
      // `2026-09-28T05:58:44Z` under "Joined" while every other table on the
      // portal printed a date and a time. A null stays null for the same
      // reason a null amount does — a player who has not deposited has no
      // first deposit, and a formatted epoch would claim they had.
      out[key] = formatDateTime(value);
    } else {
      out[key] = value;
    }
  }
  return out;
}

/**
 * §14.5d — the attribution model in plain language.
 *
 * The API already returns the sentence; this is here so a portal that decides to
 * render its own label has one that is right. "An affiliate who cannot tell
 * which model applies cannot tell whether a lost attribution was a rule or a
 * bug, and every one of those becomes a support ticket."
 */
export function attributionNotice(sentence: string): string {
  return `How you are credited: ${sentence}`;
}

/* ------------------------------------------------------------------ *
 * Tracking links — §11
 * ------------------------------------------------------------------ */

export interface LinkRow {
  readonly link_id: string;
  readonly campaign: string;
  readonly url: string;
  readonly created: string;
  readonly revoked: boolean;
  /** §11.2 — placement labels stored on this link (s1 first). */
  readonly labels: readonly string[];
  /**
   * §11.6a — the short form of the same link, when one was minted. An
   * operator can turn short links on for every link without asking; a second
   * public identifier for the affiliate's own link is one they must be able
   * to see, or the first they hear of it is a short URL in a report they
   * cannot match to anything.
   */
  readonly shortUrl?: string;
  /** Present on a revoked link: what it means for traffic already sent. */
  readonly revokedNote?: string;
  /**
   * 0138 — present when the link is alive but its clicks are being turned
   * away, in the affiliate's own words. Distinct from `revokedNote`: a
   * revoked link is dead and needs replacing, this one starts working again
   * on its own if the operator adds them back, and telling an affiliate to
   * replace a link that is about to work would waste a media buy.
   */
  readonly stoppedNote?: string;
}

/**
 * §11.5 — a revoked link is shown, marked, not hidden.
 *
 * A link that silently disappears from the list reads as a bug in the portal,
 * and the affiliate keeps paying for a placement that no longer tracks. The
 * note says the part they actually need: clicks already sent are not lost.
 */
export function linkRow(row: {
  link_id: string;
  campaign_name: string;
  brand_name: string;
  url: string;
  short_url?: string;
  created_at: string;
  revoked: boolean;
  labels?: readonly string[];
  stopped_because?: string;
}): LinkRow {
  return {
    link_id: row.link_id,
    campaign: `${row.brand_name} · ${row.campaign_name}`,
    url: row.url,
    ...(row.short_url ? { shortUrl: row.short_url } : {}),
    created: formatDate(row.created_at),
    revoked: row.revoked,
    labels: row.labels ?? [],
    ...(row.revoked
      ? {
          revokedNote: t('link.revoked'),
        }
      : {}),
    // Only when it is not revoked: a dead link needs replacing and saying
    // "this starts working again if they add you back" about one would be
    // false. Revocation is the stronger fact, so it wins.
    ...(!row.revoked && row.stopped_because ? { stoppedNote: row.stopped_because } : {}),
  };
}

/** §11.2 — the sub-ID slots, and what they are for, in the affiliate's terms. */
export const SUB_ID_SLOTS = 10;
export const SUB_ID_MAX_LENGTH = 100;

/**
 * §11.2a — checked here as well as at the API.
 *
 * Rejected rather than truncated, and said before the request rather than
 * after: a shortened sub-ID is a different sub-ID, and the affiliate's own
 * tracker will disagree with ours on a value we quietly changed.
 */
export function checkSubId(value: string, required = false): string | null {
  if (required && value.trim() === '') {
    return t('refuse.subId.required');
  }
  if (value.length > SUB_ID_MAX_LENGTH) {
    return t('refuse.subId.tooLong', { length: value.length, max: SUB_ID_MAX_LENGTH });
  }
  return null;
}

/**
 * Attach sub-IDs for display.
 *
 * The portal previews what it will look like; the API is what builds the link
 * that is stored. Two builders would disagree eventually, so this one is only
 * ever a preview and says so at its call site.
 */
export function previewSubIds(url: string, subIds: readonly string[]): string {
  const preview = new URL(url);
  subIds.forEach((s, i) => {
    if (s !== '') preview.searchParams.set(`s${i + 1}`, s);
  });
  return preview.toString();
}

/**
 * A stored minor-unit figure, back in the major unit somebody typed.
 *
 * Only for putting a saved number back into the box it came from — every
 * figure a reader *reads* goes through `formatMoney`, which groups it and
 * writes the currency in their own language. This one is deliberately plain:
 * a grouped `1,500.00` in an input is a string the form would then have to
 * parse back, and the first reader whose locale groups with a full stop would
 * have their threshold read as a different number.
 *
 * The scale comes from the currency list rather than a constant, because it is
 * 2 for USD, 0 for JPY and 3 for KWD. An unknown currency returns the figure
 * unchanged, which is wrong by a factor of a hundred but visibly so — better
 * than silently dividing by a scale that was guessed.
 */
export function majorUnits(
  minor: string,
  currency: string,
  scales: readonly { readonly code: string; readonly minor_unit_scale: number }[],
): string {
  const scale = scales.find((c) => c.code === currency)?.minor_unit_scale;
  if (scale === undefined) return minor;
  if (scale === 0) return minor;
  const negative = minor.startsWith('-');
  const digits = (negative ? minor.slice(1) : minor).padStart(scale + 1, '0');
  const whole = digits.slice(0, digits.length - scale);
  const fraction = digits.slice(digits.length - scale);
  return `${negative ? '-' : ''}${whole}.${fraction}`;
}

/* ------------------------------------------------------------------ *
 * The three things one tracking link is also addressable as
 * ------------------------------------------------------------------ */

export interface TrackingAddresses {
  /** The link itself, query string and all — what a person clicks. */
  readonly click: string;
  /** §12.12a — the pixel the edge counts an impression from. */
  readonly impression: string;
  /** The same link as a scannable square, served by the edge as SVG. */
  readonly qr: string;
}

/**
 * One link, as the three addresses the edge already answers on.
 *
 * `/c/{token}` redirects, `/i/{token}` counts an impression and `/c/{token}/qr`
 * draws the square (apps/edge-click/src/serve.ts). All three are derived here
 * rather than asked for, because they are the same token on the same host and
 * a round trip to be told so would be a round trip per banner on a page of
 * them.
 *
 * Any sub-IDs stay on the click address alone. They describe where a *click*
 * came from; the pixel counts a serve, which §12.12 is explicit has no
 * placement to attribute and nothing for a sub-ID to label, and the QR is the
 * click address drawn — the edge rebuilds it from the token and never sees
 * what was hung on the copy the affiliate held.
 *
 * `null` when the URL is not one of ours, rather than a guess: a link whose
 * shape we do not recognise is one whose token we cannot name, and a pixel
 * pointed at a token that does not exist counts nothing while looking like it
 * counts something.
 */
export function trackingAddresses(url: string): TrackingAddresses | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const token = /^\/c\/([^/]+)\/?$/.exec(parsed.pathname)?.[1];
  if (token === undefined || token === '') return null;
  const origin = parsed.origin;
  return {
    click: parsed.toString(),
    impression: `${origin}/i/${token}`,
    qr: `${origin}/c/${token}/qr`,
  };
}

/* ------------------------------------------------------------------ *
 * Copy-paste HTML (WEB-1)
 * ------------------------------------------------------------------ */

/**
 * A value made safe to put in HTML text or a double- or single-quoted
 * attribute.
 *
 * The snippets below go onto the affiliate's own site, and every value in
 * them is somebody else's: the creative's address and the campaign name are
 * the operator's, the anchor text is whatever was typed. Interpolated raw, a
 * creative address of `x"><script>…` was script on every site that pasted
 * the banner.
 */
export function snippetEscape(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * §12.13 (0219) — a banner: the creative linked through the click address,
 * plus the impression pixel. `?cr=` on both halves, so the click and the
 * impression land on the same creative and the CTR for this banner is a real
 * ratio rather than one campaign's clicks over another's impressions.
 */
export function creativeEmbedHtml(click: string, impression: string, creativeId: string, assetUri: string): string {
  const href = `${click}${click.includes('?') ? '&' : '?'}cr=${creativeId}`;
  return (
    `<a href="${snippetEscape(href)}" target="_blank" rel="noopener sponsored">` +
    `<img src="${snippetEscape(assetUri)}" alt="" /></a>` +
    `<img src="${snippetEscape(`${impression}?cr=${creativeId}`)}" width="1" height="1" alt="" />`
  );
}

/** The impression pixel on its own. */
export function impressionPixelHtml(impression: string): string {
  return `<img src="${snippetEscape(impression)}" width="1" height="1" alt="" />`;
}

/** A plain text link to the click address. */
export function textLinkHtml(click: string, text: string): string {
  return `<a href="${snippetEscape(click)}" target="_blank" rel="noopener sponsored">${snippetEscape(text)}</a>`;
}

/* ------------------------------------------------------------------ *
 * The reporting window
 * ------------------------------------------------------------------ */

/**
 * The windows the filter bar offers that are not a plain count of days back.
 *
 * A rolling count and a calendar month answer different questions, and an
 * affiliate reconciling against an operator's statement needs the second one:
 * "last 30 days" never lines up with a period that ran 1–31, so a figure
 * compared against a statement is off by whatever the overlap was. The
 * calendar entries exist so that comparison can be made at all.
 */
export const NAMED_WINDOWS = ['today', 'yesterday', 'mtd', 'last-month', 'ytd', 'custom'] as const;

export interface ReportWindow {
  readonly from: string;
  readonly to: string;
}

/** `YYYY-MM-DD`, from the same clock the rest of this window is built on. */
function isoDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

const DAY_MS = 86_400_000;

/** Whether a typed date is one this can build a window from at all. */
export function isDayInput(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));
}

/**
 * The two dates a report is asked for, from whichever window is in force.
 *
 * Everything here is UTC, because the `to` this replaces already was
 * (`now.toISOString()`), and a `from` computed in the reader's own zone
 * against a `to` computed in UTC is a window that is a day wide at the wrong
 * end for half the world — silently, and only for readers west of Greenwich.
 *
 * An unusable custom pair is not an error and does not empty the screen: it
 * falls back to the same thirty days the console opens on, so a half-typed
 * date reads as "not narrowed yet" rather than as a report with no rows. The
 * inputs are what tell the affiliate the pair is incomplete; a blank report
 * would say something else entirely.
 */
export function reportWindow(
  now: Date,
  window: string,
  custom?: { readonly from?: string; readonly to?: string },
): ReportWindow {
  const today = isoDay(now.getTime());
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();

  /*
   * A single day is a window whose two dates are the same, not a special
   * case. Every report downstream takes a closed `from`/`to` pair, so "today"
   * that reported nothing would otherwise be indistinguishable from a filter
   * that failed to apply.
   */
  if (window === 'today') {
    return { from: today, to: today };
  }
  if (window === 'yesterday') {
    const then = isoDay(now.getTime() - DAY_MS);
    return { from: then, to: then };
  }
  if (window === 'mtd') {
    return { from: isoDay(Date.UTC(year, month, 1)), to: today };
  }
  if (window === 'last-month') {
    return {
      from: isoDay(Date.UTC(year, month - 1, 1)),
      // Day zero of this month is the last day of the month before it, which
      // is the one piece of month arithmetic that never needs a length table.
      to: isoDay(Date.UTC(year, month, 0)),
    };
  }
  if (window === 'ytd') {
    return { from: isoDay(Date.UTC(year, 0, 1)), to: today };
  }
  if (window === 'custom') {
    const from = custom?.from ?? '';
    const to = custom?.to ?? '';
    if (isDayInput(from) && isDayInput(to)) {
      // Typed backwards is still a window the affiliate meant; the two dates
      // are what they chose, and only their order was wrong.
      return from <= to ? { from, to } : { from: to, to: from };
    }
    return reportWindow(now, '30');
  }

  const days = Number(window);
  const n = Number.isFinite(days) && days > 0 ? days : 30;
  return { from: isoDay(now.getTime() - (n - 1) * DAY_MS), to: today };
}

/* ------------------------------------------------------------------ *
 * How you want to be paid — §31.1, §31.1b
 * ------------------------------------------------------------------ */

/** The methods §31.1 permits. Names an affiliate recognises, not our constants. */
const PAYOUT_METHOD_CODES = [
  'BANK_TRANSFER',
  'EWALLET',
  'CRYPTO',
  'CHEQUE',
  'OTHER',
] as const;

/*
 * Built on read rather than frozen at module load: `t()` answers in whatever
 * language the tab is reading *now*, and a constant array evaluated once at
 * import would be the language the tab booted in — which is English, since the
 * saved choice arrives with `me()` a moment later.
 */
export function payoutMethods(): readonly { value: string; label: string }[] {
  return PAYOUT_METHOD_CODES.map((value) => ({
    value,
    label: t(`payout.method.${value}` as AffiliateMessageKey),
  }));
}

/**
 * §31.1b — the platform holds no payment credential, and the form must not
 * invite one.
 *
 * The database refuses a credential outright, but an affiliate who has typed
 * their IBAN into a box and been rejected has already put it in a request log
 * and a browser autofill store. Refusing it at the keystroke is the only
 * refusal that happens before it exists anywhere.
 */
const LOOKS_LIKE_A_CREDENTIAL =
  /\b([A-Z]{2}\d{2}[A-Z0-9]{10,})\b|\b\d{6,}\b|\b[A-Z]{6}[A-Z0-9]{2,5}\b/;

export function checkPayoutLabel(label: string): string | null {
  if (LOOKS_LIKE_A_CREDENTIAL.test(label.replace(/\s+/g, ''))) {
    return t('refuse.payout.credentialInLabel');
  }
  if (label.length > 60) return t('refuse.payout.labelTooLong');
  return null;
}

/**
 * #34 / §31.1b — the link to where the details are kept, checked at the
 * keystroke.
 *
 * The owner's decision on payment details was an external link only. That is
 * a URL field sitting exactly where §31.1b says a credential must never be
 * collected — "including 'optionally', 'for convenience', or in a free-text
 * note field" — so it is checked here for the same reason `checkPayoutLabel`
 * is: an affiliate who has typed their wallet address and been rejected by
 * the server has already put it in a request log and a browser autofill
 * store. The refusal that counts happens before it exists anywhere.
 *
 * Deliberately quiet while somebody is still typing. `https://b` is not a
 * usable link yet and complaining about it teaches people to ignore the
 * message; what this refuses is a value that is *finished* and wrong.
 */
const IS_AN_ADDRESS = /^\s*(0x[a-fA-F0-9]{40}|[1-9A-HJ-NP-Za-km-z]{32,44})\s*$/;
const HAS_AN_IBAN = /[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}/i;

export function checkPayoutDetailsUrl(value: string): string | null {
  const url = value.trim();
  if (url === '') return null;

  if (IS_AN_ADDRESS.test(url)) {
    return t('refuse.payout.walletAddress');
  }
  if (HAS_AN_IBAN.test(url.replace(/\s+/g, ''))) {
    return t('refuse.payout.ibanInLink');
  }
  if (url.length > 500) return t('refuse.payout.linkTooLong');

  // Only once a scheme has been typed, so the message arrives when it is
  // actionable rather than after the second character.
  if (/^[a-z][a-z0-9+.-]*:/i.test(url) && !/^https:\/\//i.test(url)) {
    return t('refuse.payout.httpsOnly');
  }
  if (/^https:\/\/\S+$/i.test(url) && !/^https:\/\/[^/\s]+\.[^/\s]+/i.test(url)) {
    return t('refuse.payout.noDomain');
  }
  return null;
}

export function checkPayoutDetailsUrlLabel(label: string, url: string): string | null {
  if (label.trim() === '') return null;
  if (url.trim() === '') return t('refuse.payout.labelWithoutLink');
  if (label.length > 60) return t('refuse.payout.labelLimit');
  if (IS_AN_ADDRESS.test(label) || HAS_AN_IBAN.test(label.replace(/\s+/g, ''))) {
    return t('refuse.payout.labelIsCredential');
  }
  return null;
}

/**
 * Ch. 33 — dispute categories and statuses in the affiliate's language.
 *
 * §33.4a again: the screen is where "our system says so" becomes an answer or
 * fails to. COMMISSION_FIGURE is our constant; "a commission figure" is what
 * they are actually arguing about.
 */
const DISPUTE_CODES: ReadonlySet<string> = new Set([
  'ATTRIBUTION',
  'COMMISSION_FIGURE',
  'DEDUCTION',
  'CLAWBACK',
  'HOLD',
  'SETTLEMENT',
  'STATUS_CHANGE',
  // Item 14 — no separate Acknowledge: RAISED moves straight to a decision.
  'RAISED',
  // Item 4 — the single-dispute endpoint answers with this instead of RAISED
  // for the same not-yet-reviewed state; the list (built from a different
  // endpoint) never sends it, which is why the detail popup read "OPEN" in
  // the server's own word while the list read "Resolved" for the same row.
  'OPEN',
  'UNDER_REVIEW',
  'NEEDS_INFO',
  // §33.9 — left the internal process for external mediation/arbitration.
  'ESCALATED',
  'ACCEPTED',
  'DECLINED',
  'RESOLVED',
  // Item 13 — reopened once; a decision on it goes to CLOSED, not RESOLVED again.
  'REOPENED',
  'CLOSED',
]);

export function disputeWords(code: string): string {
  return DISPUTE_CODES.has(code) ? t(`dispute.${code}` as AffiliateMessageKey) : code;
}

/** A Chip tone, spelled out here because present.ts stays free of kit's React types. */
type Tone = 'neutral' | 'provisional' | 'locked' | 'overdue' | 'attention';

/**
 * Item 4 (batch 2 corrected) — a dispute's status as a Chip tone, by
 * DESIGN.md §4's table, aligned with the operator console's own mapping
 * (`disputeStatusTone` in affdice-admin's App.tsx): a decided, favourable
 * status is settled (`locked`); a decline is the negative outcome
 * (`overdue`); NEEDS_INFO is the one that wants a look (`attention`) —
 * `attention` is never spent on a money state, and everything still open or
 * being looked at (RAISED, OPEN — the single-dispute endpoint's word for the
 * same not-yet-reviewed state — REOPENED, UNDER_REVIEW, ESCALATED) is
 * `neutral`, not `attention`: those are ordinary progress, not something
 * that needs the affiliate's own look.
 */
export function disputeStatusTone(code: string): Tone {
  switch (code) {
    case 'ACCEPTED':
    case 'RESOLVED':
    case 'CLOSED':
      return 'locked';
    case 'DECLINED':
      return 'overdue';
    case 'NEEDS_INFO':
      return 'attention';
    default:
      // RAISED, OPEN, UNDER_REVIEW, ESCALATED, REOPENED, and anything the
      // platform adds later: open or being looked at, not a verdict.
      return 'neutral';
  }
}

/**
 * Item 4 — the invoice's own status, which has no existing word helper the
 * way a dispute's does. The whole of 0040's vocabulary (GENERATED,
 * PENDING_REVIEW, APPROVED, ISSUED, MARKED_PAID, DISPUTED, CANCELLED) plus the
 * settlement pair; only three had words, so a paid invoice read MARKED_PAID.
 * Unmapped codes fall back to themselves, same as `disputeWords` — a status
 * the platform adds later is not ours to put words to before it exists here.
 */
const INVOICE_STATUS_CODES: ReadonlySet<string> = new Set([
  'GENERATED', 'PENDING_REVIEW', 'APPROVED', 'ISSUED', 'MARKED_PAID', 'DISPUTED', 'CANCELLED',
  'PARTIALLY_SETTLED', 'SETTLED',
]);

export function invoiceStatusWords(code: string): string {
  return INVOICE_STATUS_CODES.has(code) ? t(`invoice.status.${code}` as AffiliateMessageKey) : code;
}

/**
 * Item 4 (batch 2 corrected) — `ISSUED`/`PARTIALLY_SETTLED` were `provisional`,
 * but the invoice screen's own figures say otherwise: "neither figure is
 * provisional (an issued invoice is the billed record, not a still-open
 * period)" (App.tsx, the invoice detail modal). `provisional` means money
 * that can still move — a draft total, a forecast — and an issued invoice's
 * total does not move; only how much of it has settled does. `ISSUED` is
 * "sent" and `PARTIALLY_SETTLED` is ordinary progress toward `SETTLED`,
 * neither of which the table's `neutral` bucket excludes.
 */
export function invoiceStatusTone(code: string): Tone {
  switch (code) {
    case 'SETTLED':
    // The operator says it paid (§31.1a — their claim, not our observation),
    // and nothing is left to happen on the invoice itself.
    case 'MARKED_PAID':
      return 'locked';
    // An open question about the money, which the affiliate may be party to.
    case 'DISPUTED':
      return 'attention';
    default:
      // GENERATED, PENDING_REVIEW, APPROVED, ISSUED, PARTIALLY_SETTLED,
      // CANCELLED, and anything the platform adds later: steps on the way,
      // or a withdrawal — none a negative outcome for the affiliate.
      return 'neutral';
  }
}

/**
 * Item 4 — a player's qualification status (QUALIFIED, NOT_QUALIFIED,
 * PENDING), which has no existing word helper.
 */
const QUALIFICATION_STATUS_CODES: ReadonlySet<string> = new Set(['QUALIFIED', 'NOT_QUALIFIED', 'PENDING']);

export function qualificationStatusWords(code: string): string {
  return QUALIFICATION_STATUS_CODES.has(code)
    ? t(`qualification.status.${code}` as AffiliateMessageKey)
    : code;
}

export function qualificationStatusTone(code: string): Tone {
  switch (code) {
    case 'QUALIFIED':
      return 'locked';
    // §35.4a — an open question, not a verdict: still assessable, so it can
    // still move, the same reason a draft total is `provisional`.
    case 'PENDING':
      return 'provisional';
    default:
      // NOT_QUALIFIED — a fact about the player, not a failure of anything.
      return 'neutral';
  }
}

/**
 * Item 4 — an agent mandate's state (`m.state` was shown raw), which has no
 * existing word helper.
 */
const MANDATE_STATE_CODES: ReadonlySet<string> = new Set([
  'REQUESTED', 'GRANTED', 'USED', 'REVOKED', 'EXPIRED',
]);

export function mandateStateWords(code: string): string {
  return MANDATE_STATE_CODES.has(code) ? t(`mandate.state.${code}` as AffiliateMessageKey) : code;
}

/**
 * Item 3 (batch 2 corrected) — REQUESTED needs the affiliate's own look; USED
 * is done, not live. REVOKED and EXPIRED were `overdue`, but that tone is for
 * a negative outcome (failed, late, blocked, rejected) and neither of these
 * is one: REVOKED is the affiliate's own choice, and EXPIRED simply lapsed —
 * DESIGN.md §4's `neutral` bucket names both "revoked-by-choice" and
 * "expired" directly.
 */
export function mandateStateTone(code: string): Tone {
  switch (code) {
    case 'GRANTED':
      return 'locked';
    case 'REQUESTED':
      return 'attention';
    default:
      // USED, REVOKED, EXPIRED — no longer an open request, and none of the
      // three is a negative outcome.
      return 'neutral';
  }
}

export function invoiceRow(row: {
  invoice_number: string;
  status: string;
  total_minor: string;
  currency: string;
  due_at: string;
  settled_minor: string;
}): { number: string; status: string; total: string; due: string; settled: string } {
  return {
    number: row.invoice_number,
    status: row.status,
    total: formatMoney({ amount_minor: row.total_minor, currency: row.currency }),
    due: formatDate(row.due_at),
    settled: formatMoney({ amount_minor: row.settled_minor, currency: row.currency }),
  };
}

export function invoiceDetailView(row: {
  invoice_number: string;
  status: string;
  subtotal_minor: string;
  tax_minor: string;
  total_minor: string;
  currency: string;
  issued_at: string;
  due_at: string;
  settled_minor: string;
}): {
  number: string;
  status: string;
  subtotal: string;
  tax: string;
  total: string;
  settled: string;
  issued: string;
  due: string;
} {
  return {
    number: row.invoice_number,
    status: row.status,
    subtotal: formatMoney({ amount_minor: row.subtotal_minor, currency: row.currency }),
    tax: formatMoney({ amount_minor: row.tax_minor, currency: row.currency }),
    total: formatMoney({ amount_minor: row.total_minor, currency: row.currency }),
    settled: formatMoney({ amount_minor: row.settled_minor, currency: row.currency }),
    issued: formatDate(row.issued_at),
    due: formatDate(row.due_at),
  };
}

export function disputeDetailAmount(
  amountMinor: string | null,
  currency: string | null,
): string {
  if (amountMinor === null || currency === null) return '—';
  return formatMoney({ amount_minor: amountMinor, currency });
}

export function disputeDetailLine(row: {
  category: string;
  status: string;
  disputed_amount_minor: string | null;
  currency: string | null;
  resolution: string | null;
  note: string;
}): { summary: string } {
  const amount = disputeDetailAmount(row.disputed_amount_minor, row.currency);
  const amountBit = amount === '—' ? '' : ` · ${amount}`;
  return {
    summary: `${disputeWords(row.category)} · ${disputeWords(row.status)}${amountBit}. ${row.resolution ?? row.note}`,
  };
}

/**
 * §23.8 — the calculation trace, as the rows an affiliate reads: whose player,
 * where, on which product, under which deal version and period, the basis and
 * rate, which CPA price row applied, the amount and when it was computed —
 * then the steps, and the deductions that actually moved money.
 *
 * Every field is optional. The trace used to carry only type, amount and a
 * note, and the player/country/product/version/period/price-row fields arrive
 * with a later API; a field the response does not carry is a row this view
 * leaves out, never a row reading "undefined" or a dash that claims to know.
 *
 * Never `rule_ids`, and never the server's note: the note names "rule IDs"
 * this screen deliberately does not show, and a rule number from a document
 * the affiliate has never seen answers nothing (owner: no § on any surface).
 * Anything else read from the payload as text goes through
 * `stripRuleReferences` all the same.
 */
export interface CommissionTraceView {
  readonly rows: readonly { label: string; value: string }[];
  readonly steps: readonly string[];
  readonly breakdown: readonly { label: string; value: string }[];
}

export function commissionTraceRows(raw: Record<string, unknown>): CommissionTraceView {
  const text = (v: unknown): string | null => {
    if (v === null || v === undefined) return null;
    const s = stripRuleReferences(String(v)).trim();
    return s === '' ? null : s;
  };
  const minor = (v: unknown): string | null => {
    const s = v === null || v === undefined ? '' : String(v).trim();
    return /^-?\d+$/.test(s) ? s : null;
  };
  const known = (key: string): key is AffiliateMessageKey => key in AFFILIATE_MESSAGES;
  const currency = text(raw['currency']) ?? '';
  const money = (m: string) => formatMoney({ amount_minor: m, currency });

  const rows: { label: string; value: string }[] = [];
  const push = (key: AffiliateMessageKey, value: string | null) => {
    if (value !== null) rows.push({ label: t(key), value });
  };

  const type = text(raw['type']);
  const amount = minor(raw['amount_minor']);
  if (type) {
    // A carryover's sign is the direction the deficit moved (§27.6).
    const sign = amount === null ? 0 : BigInt(amount) < 0n ? -1 : BigInt(amount) > 0n ? 1 : 0;
    const key = type === 'CARRYOVER' && sign !== 0
      ? (sign < 0 ? 'kind.CARRYOVER.deficit' : 'kind.CARRYOVER.repaid')
      : `kind.${type}`;
    push('trace.row.type', known(key) ? t(key) : type);
  }
  push('trace.row.player', text(raw['player_ref']));
  const jurisdiction = text(raw['jurisdiction']);
  push('trace.row.country', jurisdiction ? geoLabel(jurisdiction) : null);
  push('trace.row.product', text(raw['product']));
  const version = raw['deal_version'];
  push('trace.row.dealVersion', typeof version === 'number' || (typeof version === 'string' && /^\d+$/.test(version))
    ? t('trace.dealVersion', { n: String(version) })
    : null);

  const startsAt = text(raw['period_starts_at'] ?? raw['starts_at']);
  const endsAt = text(raw['period_ends_at'] ?? raw['ends_at']);
  if (startsAt && endsAt) {
    // The days it covers, not the instants stored: the window is half-open.
    const { first, last } = periodDays(startsAt, endsAt);
    push('trace.row.period', first === last
      ? formatDay(first)
      : t('statements.periodRange', { from: formatDay(first), to: formatDay(last) }));
  }

  const basis = minor(raw['basis_minor'] ?? raw['basis_amount_minor']);
  if (basis !== null) {
    const kind = text(raw['basis_kind']);
    const kindKey = kind ? `basis.${kind}` : null;
    const words = kindKey ? (known(kindKey) ? t(kindKey) : kind) : null;
    push('trace.row.basis', words ? `${words} · ${money(basis)}` : money(basis));
  }
  const rate = raw['rate_bps'];
  if (typeof rate === 'number' || (typeof rate === 'string' && /^\d+$/.test(rate))) {
    push('trace.row.rate', `${(Number(rate) / 100).toFixed(2)}%`);
  }

  // Present only on an acquisition line (CPA or hybrid CPA), and only read
  // for one: `null` is "no price row matched, the deal's base CPA paid";
  // absent is "this response does not say", which is no row at all. Any
  // other line has no price list, so a key left on it says nothing.
  const cpaShaped = type === 'CPA' || type === 'HYBRID_CPA';
  if (cpaShaped && 'price_row_applied' in raw) {
    const applied = raw['price_row_applied'];
    if (applied === null) {
      push('trace.row.priceRow', t('trace.priceRow.base'));
    } else if (typeof applied === 'object') {
      const r = applied as Record<string, unknown>;
      const j = text(r['jurisdiction']);
      const c = text(r['channel']);
      const m = minor(r['amount_minor']);
      const where = [
        j && j !== '*' ? geoLabel(j) : null,
        c && c !== '*' ? c : null,
      ].filter((x): x is string => x !== null).join(' · ');
      const label = where || t('terms.cpaPriceList.anyJurisdiction');
      push('trace.row.priceRow', m !== null ? `${label} — ${money(m)}` : label);
    }
  }

  push('trace.row.amount', amount !== null ? money(amount) : null);
  const computedAt = text(raw['computed_at']);
  push('trace.row.computedAt', computedAt ? formatDateTime(computedAt) : null);

  const steps = (Array.isArray(raw['steps']) ? raw['steps'] : [])
    .map((st) => {
      const code = text(st !== null && typeof st === 'object' ? (st as Record<string, unknown>)['step'] : st);
      if (!code) return null;
      const key = `trace.step.${code}`;
      return known(key) ? t(key) : code;
    })
    .filter((x): x is string => x !== null);

  // A deduction of nothing is a placeholder the engine writes, not a charge;
  // listing it as "Administrative fee 0.00" reads as a fee somebody applied.
  const breakdown = (Array.isArray(raw['basis_breakdown']) ? raw['basis_breakdown'] : [])
    .map((b) => (b !== null && typeof b === 'object' ? (b as Record<string, unknown>) : null))
    .filter((b): b is Record<string, unknown> => b !== null)
    .flatMap((b) => {
      const m = minor(b['amount_minor']);
      if (m === null || BigInt(m) === 0n) return [];
      const item = text(b['item']) ?? '';
      const key = `deductionItem.${item}`;
      return [{ label: known(key) ? t(key) : item, value: money(m) }];
    });

  return { rows, steps, breakdown };
}

/**
 * Whose logo the rail wears. One brand in view — the affiliate's only brand,
 * or the one they picked — shows that brand's logo; "All brands" has no single
 * logo to show, and the product mark stands.
 */
export function railBrandLogo(
  brands: readonly { readonly brand_id: string; readonly logo_url?: string | null }[],
  brandId: string,
): string | null {
  const one = brands.length === 1 ? brands[0] : brands.find((b) => b.brand_id === brandId);
  return one?.logo_url ?? null;
}

/**
 * AF-01 WP9 (owner, 2026-10-16) — who asked, on a row AffDice platform staff
 * made (an agent mandate). The API flags such a row and leaves the person
 * out; the affiliate sees the team, never a staff account.
 */
export function actorName(
  name: string | null | undefined,
  platform: boolean | null | undefined,
  fallback = '—',
): string {
  if (platform) return t('actor.affdiceTeam');
  return name ?? fallback;
}
